// src/components/chat/FactCheckButton.tsx
import * as React from "react";
import { createLogger } from "../../utils/logger";
import { DOUBLE_CHECK_SUFFIX } from "../../config/constants";

const log = createLogger("FactCheckButton");

interface FactCheckButtonProps {
  messageContent: string;
  onFactCheck?: (validationPrompt: string) => void;
  className?: string;
}

/**
 * Fact-check button that validates AI responses against industry sources
 * Sends a validation query to check statements against trusted sources
 */
const FactCheckButton: React.FC<FactCheckButtonProps> = ({
  messageContent,
  onFactCheck,
  className = "",
}) => {
  // Track fact-check state for user feedback
  const [checkState, setCheckState] = React.useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [isHovered, setIsHovered] = React.useState(false);
  const [validationResult, setValidationResult] = React.useState<string | null>(null);
  const [showResult, setShowResult] = React.useState(false);

  // Auto-reset check state after showing feedback
  React.useEffect(() => {
    if (checkState === "success" || checkState === "error") {
      const timer = setTimeout(() => {
        setCheckState("idle");
        setShowResult(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [checkState]);

  /**
   * Handle fact-check button click
   * Sends only DOUBLE_CHECK_SUFFIX as the message to trigger fact-checking
   */
  const handleFactCheck = async () => {
    if (!messageContent || messageContent.trim().length === 0) {
      log.warn("Attempted to fact-check empty message");
      setCheckState("error");
      return;
    }

    log.info("Starting fact-check operation", {
      messageLength: messageContent.length,
      messagePreview: messageContent.substring(0, 50) + (messageContent.length > 50 ? "..." : ""),
    });

    setCheckState("loading");
    setShowResult(false);

    try {
      // Send only DOUBLE_CHECK_SUFFIX as the message
      // This will be displayed as "Double checking the fact in your response..." in chat history
      const factCheckQuery = DOUBLE_CHECK_SUFFIX;

      log.debug("Fact-check query constructed", {
        queryLength: factCheckQuery.length,
      });

      // Trigger the fact-check callback if provided
      if (onFactCheck) {
        onFactCheck(factCheckQuery);
      }

      // Show success state without displaying a message
      setCheckState("success");

      log.info("Fact-check request sent successfully");
    } catch (error) {
      log.error("Fact-check failed with exception", {
        error: error instanceof Error ? error.message : String(error),
      });
      setCheckState("error");
      setValidationResult("Fact-check failed");
      setShowResult(true);
    }
  };

  const renderIcon = () => {
    switch (checkState) {
      case "loading":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              animation: "spin 1s linear infinite",
            }}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="60"
              strokeDashoffset="15"
              fill="none"
            />
          </svg>
        );
      case "success":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 6L9 17L4 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "error":
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        // Fact-check icon (document with check and X)
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Document lines */}
            <line
              x1="5"
              y1="8"
              x2="11"
              y2="8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="5"
              y1="16"
              x2="11"
              y2="16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Checkmark */}
            <polyline
              points="14 8 16 11 20 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            {/* X mark */}
            <line
              x1="15"
              y1="15"
              x2="19"
              y2="19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="19"
              y1="15"
              x2="15"
              y2="19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  const getButtonColor = () => {
    switch (checkState) {
      case "loading":
        return "#0066cc";
      case "success":
        return "#00aa00";
      case "error":
        return "#cc0000";
      default:
        return isHovered ? "#0066cc" : "#666";
    }
  };

  const getTooltipText = () => {
    switch (checkState) {
      case "loading":
        return "Checking...";
      case "success":
        return "Fact-check complete";
      case "error":
        return "Fact-check failed";
      default:
        return "Fact-check against industry sources";
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <button
        className={className}
        onClick={handleFactCheck}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={checkState === "loading"}
        title={getTooltipText()}
        aria-label={getTooltipText()}
        style={{
          backgroundColor: "transparent",
          border: "none",
          borderRadius: "4px",
          padding: "6px",
          cursor: checkState === "loading" ? "default" : "pointer",
          color: getButtonColor(),
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: checkState === "loading" ? 0.7 : 1,
          ...(isHovered && checkState === "idle"
            ? {
                backgroundColor: "#f0f0f0",
              }
            : {}),
        }}
      >
        {renderIcon()}
      </button>

      {/* Show validation result if available */}
      {showResult && validationResult && (
        <div
          style={{
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: checkState === "error" ? "#fff5f5" : "#f0f9ff",
            border: `1px solid ${checkState === "error" ? "#ffcccc" : "#cce5ff"}`,
            borderRadius: "4px",
            fontSize: "12px",
            color: checkState === "error" ? "#cc0000" : "#0066cc",
          }}
        >
          {validationResult}
        </div>
      )}
    </>
  );
};

export default FactCheckButton;
